import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-avatar-menu.css?inline';

/**
 * `<pk-avatar-menu>` — upper-right account dropdown.
 *
 * Renders a circle with initials derived from the `email` attribute
 * (first two characters of the local part, upper-cased). Clicking opens
 * a <details> dropdown with the full email, a Profile link, and a Sign out link.
 * Closes on outside click and Escape key.
 *
 * Attributes:
 *   - `email`        — REQUIRED. The user's email address.
 *   - `profile-url`  — href for the "Profile" link (default "/portal/profile")
 *   - `logout-url`   — href for the "Sign out" link (default "/portal/logout")
 *   - `avatar-url`   — when set, renders an <img> avatar instead of the initials
 *                      circle. Used to preserve Gravatar in Momentus's avatar
 *                      menu. On image-load error (broken / 404) the component
 *                      transparently falls back to the initials view.
 */
export class PkAvatarMenu extends HTMLElement {
    static readonly tagName = 'pk-avatar-menu';

    static get observedAttributes(): string[] {
        return ['email', 'profile-url', 'logout-url', 'avatar-url'];
    }

    private readonly _root: ShadowRoot;
    private readonly _outsideClickHandler = (e: MouseEvent): void => this._handleOutsideClick(e);
    private readonly _keydownHandler      = (e: KeyboardEvent): void => this._handleKeydown(e);
    private _avatarBroken = false;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
        document.addEventListener('click', this._outsideClickHandler);
        document.addEventListener('keydown', this._keydownHandler);
    }

    disconnectedCallback(): void {
        document.removeEventListener('click', this._outsideClickHandler);
        document.removeEventListener('keydown', this._keydownHandler);
    }

    attributeChangedCallback(name: string): void {
        if (name === 'avatar-url') {
            // A fresh URL gets a fresh chance to render — reset the broken flag.
            this._avatarBroken = false;
        }
        this.render();
    }

    private readonly _onAvatarError = (): void => {
        this._avatarBroken = true;
        this.render();
    };

    /** Returns the 1–2 char initials from the email local part. */
    private _initials(): string {
        const email = this.getAttribute('email') ?? '';
        const local = email.split('@')[0] ?? '';
        return local.slice(0, 2).toUpperCase() || '?';
    }

    private _handleOutsideClick(e: MouseEvent): void {
        const details = this._root.querySelector('details');
        if (!details?.open) return;
        // If the click is inside this host, ignore
        if (e.composedPath().includes(this)) return;
        details.open = false;
    }

    private _handleKeydown(e: KeyboardEvent): void {
        if (e.key !== 'Escape') return;
        const details = this._root.querySelector('details');
        if (details?.open) {
            details.open = false;
        }
    }

    private template(): TemplateResult {
        const initials    = this._initials();
        const email       = this.getAttribute('email') ?? '';
        const profileUrl  = this.getAttribute('profile-url') ?? '/portal/profile';
        const logoutUrl   = this.getAttribute('logout-url')  ?? '/portal/logout';
        const avatarUrl   = this.getAttribute('avatar-url')  ?? '';
        const showImage   = avatarUrl.length > 0 && !this._avatarBroken;

        return html`
            <style>${styles}</style>
            <details>
                <summary aria-label="Account menu for ${email}">
                    ${showImage
                        ? html`<img
                            class="avatar avatar-img"
                            src=${avatarUrl}
                            alt=""
                            aria-hidden="true"
                            referrerpolicy="no-referrer"
                            @error=${this._onAvatarError}
                        />`
                        : html`<span class="avatar" aria-hidden="true">${initials}</span>`
                    }
                </summary>
                <div class="dropdown" role="menu">
                    <p class="email-display">${email}</p>
                    <a href=${profileUrl} class="menu-item" role="menuitem">Profile</a>
                    <a href=${logoutUrl}  class="menu-item menu-item--danger" role="menuitem">Sign out</a>
                </div>
            </details>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkAvatarMenu.tagName)) {
    customElements.define(PkAvatarMenu.tagName, PkAvatarMenu);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-avatar-menu': PkAvatarMenu;
    }
}
